import {
  inject,
  Injectable,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import { UserStore } from '../stores/user.store';
import { PlacesStore } from '../stores/places.store';
import { BookingsStore } from '../stores/bookings.store';
import { FloorplanStore } from '../stores/floorplan.store';
import { AttendeesStore } from '../stores/attendees.store';
import { AvatarService } from './avatarService/avatar.service';
import { WebSocketService } from './web-socket.service';
import { FirstAiderStore } from '../stores/firstaider.store';
import { UnleashService } from './unleash/unleash.service';
import { ToastService } from './toast-service/toast.service';
import { PushNotificationService } from './push-notifications/push-notification.service';
import { ApiService } from './api-services/api.service';
import { App } from '@capacitor/app';
import { Device } from '@capacitor/device';
import { firstValueFrom } from 'rxjs';

export type InitializationStatus =
  | 'pending'
  | 'loading'
  | 'completed'
  | 'error';

@Injectable({
  providedIn: 'root',
})
export class AppInitializerService {
  // Services
  private userStore: UserStore = inject(UserStore);
  private placesStore: PlacesStore = inject(PlacesStore);
  private bookingsStore: BookingsStore = inject(BookingsStore);
  private floorplanStore: FloorplanStore = inject(FloorplanStore);
  private attendeesStore: AttendeesStore = inject(AttendeesStore);
  private avatarService: AvatarService = inject(AvatarService);
  private webSocketService = inject(WebSocketService);
  private firstAiderStore: FirstAiderStore = inject(FirstAiderStore);
  private unleashService: UnleashService = inject(UnleashService);
  private toastService = inject(ToastService);
  private pushNotificationService: PushNotificationService = inject(PushNotificationService);
  private apiService: ApiService = inject(ApiService);
  // Signals
  private _initializationStatus: WritableSignal<InitializationStatus> =
    signal<InitializationStatus>('pending');
  private _initializationError: WritableSignal<string | null> = signal<
    string | null
  >(null);
  private _currentLoadingStep: WritableSignal<string> = signal<string>('');
  // Public readonly signals
  public initializationStatus: Signal<InitializationStatus> =
    this._initializationStatus.asReadonly();
  public initializationError: Signal<string | null> =
    this._initializationError.asReadonly();
  public currentLoadingStep: Signal<string> =
    this._currentLoadingStep.asReadonly();

  /**
   * Initialize the application by loading essential data
   * Returns a promise that resolves when initialization is complete
   */
  async initializeApp(): Promise<void> {
    if (this._initializationStatus() === 'completed') {
      return; // Already initialized
    }
    this._initializationStatus.set('loading');
    this._initializationError.set(null);

    await this.unleashService.IntiateUnLeash();

    // Since we fetch data at once in parallel, showing a message for the correct data being fetched is not possible
    // We display messages sequentially and then keep the last message displayed until loading completes.
    const loadingSteps = [
      'appLoading.loadingUserInformation',
      'appLoading.loadingPreferences',
      'appLoading.loadingFloorPlans',
      'appLoading.loadingAvatars',
    ];
    
    // Show the first message immediately
    this._currentLoadingStep.set(loadingSteps[0]);
    let currentStepIndex = 1;
    const messageInterval = setInterval(() => {
      if (currentStepIndex < loadingSteps.length) {
        this._currentLoadingStep.set(loadingSteps[currentStepIndex]);
        currentStepIndex++;
      }
      // Once we've shown all messages, keep the last one displayed
      // No need to clear interval here, it will be cleared when loading completes
    }, 250); 

    try {
      // Load essential data in parallel
      const promises: Promise<any>[] = [
        this.userStore.getUserInformation(),
        this.userStore.getUserPreferences(),
        this.floorplanStore.getFloorplanList(),
        this.avatarService.fetchAvatarsForPlaces(),
      ];
      await Promise.all(promises);

      clearInterval(messageInterval);
      this._currentLoadingStep.set('');
      this._initializationStatus.set('completed');

      
      // fetch the rest of the data but we dont wait for them
      this.placesStore.fetchAvailablePlaces();
      this.bookingsStore.getMyBookings();
      this.attendeesStore.getAttendeesList();
      this.pushNotificationService.initialize();
      // this.firstAiderStore.fetchFirstAiders(); we fetch it in the page when we visit it because we need the places store to be initialized

      this.registerDevice().then((userDeviceId) => {
        this.webSocketService.createConnection(userDeviceId);
      });
    } catch (error) {
      // Clear the message interval on error as well
      clearInterval(messageInterval);
      console.error('App initialization failed:', error);
      const errorMsg =
        error instanceof Error ? error.message : 'Unknown initialization error';
      this._initializationError.set(errorMsg);
      this._initializationStatus.set('error');
      this._currentLoadingStep.set('');
 
    }
  }

  /**
   * Check if the app is initialized
   */
  isInitialized(): boolean {
    return this._initializationStatus() === 'completed';
  }

  /**
   * Check if initialization is in progress
   */
  isInitializing(): boolean {
    return this._initializationStatus() === 'loading';
  }

  /**
   * Check if initialization failed
   */
  hasInitializationError(): boolean {
    return this._initializationStatus() === 'error';
  }

  private async registerDevice(): Promise<number | undefined> {
    try {
      const [deviceInfo, appInfo] = await Promise.all([
        Device.getInfo(),
        App.getInfo(),
      ]);
      const userDevice = await firstValueFrom(
        this.apiService.postUserDevice({
          platform: deviceInfo.platform,
          appVersion: appInfo.version,
          deviceName: deviceInfo.name ?? deviceInfo.model,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        })
      );
      return userDevice?.id ? Number(userDevice.id) : undefined;
    } catch (error) {
      console.error('Failed to register device:', error);
      return undefined;
    }
  }

  /**
   * Reset initialization status (useful for logout/re-login scenarios)
   */
  resetInitialization(): void {
    this.userStore.resetStoreData();
    this.placesStore.resetStoreData();
    this.bookingsStore.resetStoreData();
    this.floorplanStore.resetStoreData();
    this.attendeesStore.resetStoreData();
    this.avatarService.resetAvatarData();
    this.firstAiderStore.resetStoreData();
    localStorage.clear();

    this._initializationStatus.set('pending');
    this._initializationError.set(null);
    this._currentLoadingStep.set('');
  }
}
